import { useState } from 'react';
import { Button, Input, useToast, PhoneInput } from '@caruso/ui';
import { CreditCard, Lock } from 'lucide-react';
import InputMask from 'react-input-mask';
import { CreditCardData, CreditCardHolderInfo } from '@/services/payments.service';
import 'react-phone-number-input/style.css';

interface CreditCardPaymentProps {
  onSubmit: (
    creditCard: CreditCardData,
    holderInfo: CreditCardHolderInfo,
    installments: number
  ) => Promise<void>;
  totalAmount: number;
  isProcessing: boolean;
}

export function CreditCardPayment({
  onSubmit,
  totalAmount,
  isProcessing,
}: CreditCardPaymentProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    ccv: '',
    cpfCnpj: '',
    postalCode: '',
    addressNumber: '',
    phone: '',
  });
  const [installments, setInstallments] = useState(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.holderName || formData.holderName.length < 3) {
      toast({
        title: 'Nome inválido',
        description: 'Digite o nome completo do titular do cartão',
        variant: 'destructive',
      });
      return;
    }

    const cardNumber = formData.number.replace(/\s/g, '');
    if (cardNumber.length < 13) {
      toast({
        title: 'Número do cartão inválido',
        description: 'Digite um número de cartão válido',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.expiryMonth || !formData.expiryYear) {
      toast({
        title: 'Validade inválida',
        description: 'Digite a validade do cartão',
        variant: 'destructive',
      });
      return;
    }

    if (formData.ccv.length < 3) {
      toast({
        title: 'CVV inválido',
        description: 'Digite o código de segurança do cartão',
        variant: 'destructive',
      });
      return;
    }

    const cpfCnpj = formData.cpfCnpj.replace(/\D/g, '');
    if (cpfCnpj.length !== 11 && cpfCnpj.length !== 14) {
      toast({
        title: 'CPF/CNPJ inválido',
        description: 'Digite um CPF ou CNPJ válido',
        variant: 'destructive',
      });
      return;
    }

    try {
      await onSubmit(
        {
          holderName: formData.holderName,
          number: cardNumber,
          expiryMonth: formData.expiryMonth,
          expiryYear: formData.expiryYear,
          ccv: formData.ccv,
        },
        {
          cpfCnpj,
          postalCode: formData.postalCode.replace(/\D/g, ''),
          addressNumber: formData.addressNumber,
          phone: formData.phone, // Já vem em formato E.164 do PhoneInput
        },
        installments
      );
    } catch (error) {
      // Error handled by parent component
    }
  };

  const calculateInstallmentValue = (installmentCount: number) => {
    return totalAmount / installmentCount;
  };

  const formatInstallmentOption = (installmentCount: number) => {
    const value = calculateInstallmentValue(installmentCount);
    return `${installmentCount}x de R$ ${value.toFixed(2).replace('.', ',')}${
      installmentCount === 1 ? ' (à vista)' : ''
    }`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Número do Cartão */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
          Número do Cartão *
        </label>
        <InputMask
          mask="9999 9999 9999 9999"
          value={formData.number}
          onChange={(e) => setFormData({ ...formData, number: e.target.value })}
        >
          {/* @ts-ignore */}
          {(inputProps) => (
            <div className="relative">
              <Input {...inputProps} placeholder="0000 0000 0000 0000" required className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory" />
              <CreditCard className="absolute right-3 top-3 w-5 h-5 text-brand-gold/50" />
            </div>
          )}
        </InputMask>
      </div>

      {/* Nome do Titular */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
          Nome do Titular *
        </label>
        <Input
          value={formData.holderName}
          onChange={(e) =>
            setFormData({ ...formData, holderName: e.target.value.toUpperCase() })
          }
          placeholder="NOME COMO ESTÁ NO CARTÃO"
          required
          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
        />
      </div>

      {/* Validade e CVV */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
            Mês *
          </label>
          <Input
            type="text"
            maxLength={2}
            value={formData.expiryMonth}
            onChange={(e) =>
              setFormData({ ...formData, expiryMonth: e.target.value })
            }
            placeholder="MM"
            required
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
            Ano *
          </label>
          <Input
            type="text"
            maxLength={4}
            value={formData.expiryYear}
            onChange={(e) =>
              setFormData({ ...formData, expiryYear: e.target.value })
            }
            placeholder="AAAA"
            required
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
            CVV *
          </label>
          <Input
            type="text"
            maxLength={4}
            value={formData.ccv}
            onChange={(e) => setFormData({ ...formData, ccv: e.target.value })}
            placeholder="123"
            required
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
        </div>
      </div>

      {/* CPF/CNPJ do Titular */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
          CPF/CNPJ do Titular *
        </label>
        <InputMask
          mask={
            formData.cpfCnpj.replace(/\D/g, '').length > 11
              ? '99.999.999/9999-99'
              : '999.999.999-999'
          }
          value={formData.cpfCnpj}
          onChange={(e) => setFormData({ ...formData, cpfCnpj: e.target.value })}
        >
          {/* @ts-ignore */}
          {(inputProps) => <Input {...inputProps} placeholder="000.000.000-00" required className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory" />}
        </InputMask>
      </div>

      {/* CEP e Número */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
            CEP do Titular *
          </label>
          <InputMask
            mask="99999-999"
            value={formData.postalCode}
            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
          >
            {/* @ts-ignore */}
            {(inputProps) => <Input {...inputProps} placeholder="00000-000" required className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory" />}
          </InputMask>
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
            Número *
          </label>
          <Input
            value={formData.addressNumber}
            onChange={(e) =>
              setFormData({ ...formData, addressNumber: e.target.value })
            }
            placeholder="123"
            required
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
        </div>
      </div>

      {/* Telefone */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
          Telefone do Titular *
        </label>
        <PhoneInput
          value={formData.phone}
          onChange={(value) => setFormData({ ...formData, phone: value || '' })}
          defaultCountry="BR"
          placeholder="Digite o telefone"
        />
      </div>

      {/* Parcelas */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-1">
          Número de Parcelas
        </label>
        <select
          value={installments}
          onChange={(e) => setInstallments(Number(e.target.value))}
          className="w-full bg-brand-smoke/50 border border-brand-gold/20 text-brand-ivory rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((count) => (
            <option key={count} value={count}>
              {formatInstallmentOption(count)}
            </option>
          ))}
        </select>
      </div>

      {/* Segurança */}
      <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-lg p-4">
        <div className="flex items-center gap-2 text-brand-gold text-sm">
          <Lock className="w-4 h-4" />
          <span className="font-medium">Pagamento 100% seguro</span>
        </div>
        <p className="text-xs text-brand-ivory/60 mt-1">
          Seus dados são criptografados e protegidos
        </p>
      </div>

      {/* Botão de Pagamento */}
      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={isProcessing}
        variant="premium"
      >
        {isProcessing ? (
          <>Processando pagamento...</>
        ) : (
          <>
            <Lock className="w-4 h-4 mr-2" />
            Pagar R$ {totalAmount.toFixed(2).replace('.', ',')}
          </>
        )}
      </Button>
    </form>
  );
}
